































































































































import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import BpcInput from '@shared/components/forms/inputs/input/input.vue';
import BpcEntitySelect from '@shared/components/forms/inputs/entity-select/entity-select.vue';
import BpcDatepicker from '@shared/components/forms/inputs/datepicker/datepicker.vue';
import BpcSwitch from '@shared/components/forms/inputs/switch/switch.vue';
import { Subscription } from '@subscriptions/interfaces/subscription';
import moment from 'moment';
import { CenterSetting } from '@core/interfaces/center-setting';

@Component({
  name: 'bpc-recredit-subscription-modal',
  components: {
    BpcInput,
    BpcEntitySelect,
    BpcDatepicker,
    BpcSwitch,
  },
})
export default class RecreditSubscriptionModal extends Vue {
  // PROPS
  @Prop({ type: Boolean, default: false }) readonly showModal: boolean;
  @Prop({ type: Number, required: true }) readonly subscriptionId: number;
  @Prop({ type: Boolean, default: false }) readonly online: boolean;

  creditsToAdd = 0;
  saving = false;
  subscription: Subscription = null;
  subscriptionLoaded = false;

  formModel: {
    remainingCredits: number;
    totalCredits: number;
    creditsToAdd: number;
    code: string;
    priceCategoryId: number;
    endDate: string;
    notifyByEmail: boolean;
    articleId: number;
    startDate: string;
    categoryName: string;
    categoryId: number;
    contactId: number;
  } = {
    code: null,
    startDate: null,
    endDate: null,
    totalCredits: null,
    creditsToAdd: null,
    remainingCredits: null,
    articleId: null,
    priceCategoryId: null,
    notifyByEmail: false,
    categoryName: null,
    categoryId: null,
    contactId: null,
  };

  closeModal() {
    this.$emit('close');
    this.cleanFormModel();
    this.creditsToAdd = 0;
  }

  get strToDetectIfRefreshFormModelIsNeeded(): string {
    // We need to refresh the entity if the subscriptionId or the showModal
    // change, to avoid multiple calls we create this computed property
    return [this.subscriptionId, this.showModal ? 'show' : 'hide'].join('-');
  }

  get noLabel() {
    // TODO initialize correctly the "for" attribute in field-wrapper
    return {
      labelAttributes: {
        for: 'test',
      },
    };
  }

  get col6() {
    return {
      wrapperAttributes: {
        class: {
          'inline-field col-md-6 pl-2': true,
        },
      },
    };
  }
  get col12() {
    return {
      wrapperAttributes: {
        class: {
          'inline-field col-md-12 pl-2': true,
        },
      },
    };
  }
  get col3() {
    return {
      wrapperAttributes: {
        class: {
          'inline-field col-md-3 pl-2': true,
        },
      },
    };
  }
  get col4() {
    return {
      wrapperAttributes: {
        class: {
          'inline-field col-md-4 pl-2': true,
        },
      },
    };
  }

  get centerSetting(): CenterSetting {
    return this.$store.getters['centerSettings/centerSettings'];
  }

  mounted() {
    this.getSubscription();
  }

  async onConfirmBackOffice() {
    const basePath = this.$bpcApiRouteHelper.getEntityPath('subscription');
    const path = `${basePath}/recredit/${this.subscriptionId}`;
    const body = {
      creditsToAdd: this.formModel.creditsToAdd,
      endDate: this.formModel.endDate,
      articleId: this.formModel.articleId,
      priceCategoryId: this.formModel.priceCategoryId,
      notifyByEmail: this.formModel.notifyByEmail,
    };
    try {
      const res = await this.$bpcApi.post(path, body).toPromise();
      // console.log(res.data);
      this.$emit('recredit-success', res.data);
      this.closeModal();
    } catch (error) {
      this.handleError(error);
    }
  }

  async onConfirmFrontOffice() {
    const basePath = this.$bpcApiRouteHelper.getApiPath('shopping-cart/add-product');
    const body = {
      productType: 'subscription',
      productCategoryId: this.formModel.categoryId,
      contactId: this.formModel.contactId,
      additionalParameters: {
        subscriptionToRecredit: this.subscriptionId,
      },
    };
    try {
      const res = await this.$bpcApi.post(basePath, body).toPromise();
      // console.log(res.data);
      this.$emit('recredit-success', res.data);
      this.$emit('success-subscription-creation-in-front-office', res.data);
      this.closeModal();
    } catch (error) {
      this.handleError(error);
    }
  }

  handleError(error) {
    console.log(error);
  }

  async getSubscription() {
    this.subscriptionLoaded = false;
    this.cleanFormModel();
    const basePath = this.$bpcApiRouteHelper.getEntityPath('subscription');
    const path = `${basePath}/${this.subscriptionId}`;
    const res = await this.$bpcApi.get(path).toPromise();
    this.subscription = res.data;
    this.setInitialFormValues();
    this.subscriptionLoaded = true;
  }

  setInitialFormValues() {
    const recreditStartDate = this.getRecreditStartDate();

    this.formModel = {
      code: this.subscription.code,
      startDate: recreditStartDate,
      endDate: this.getDefaultEndDate(recreditStartDate),
      totalCredits: this.subscription.total_credits,
      creditsToAdd: this.getDefaultCreditsToAdd(),
      remainingCredits: this.subscription.remaining_credits,
      articleId: this.subscription.article?.id,
      priceCategoryId: this.subscription.price_category?.id,
      notifyByEmail: this.centerSetting.admin_send_subscription_recredit_email,
      categoryName: this.subscription.subscription_category?.name,
      categoryId: this.subscription.subscription_category?.id,
      contactId: this.subscription.contact_id,
    };
  }

  cleanFormModel() {
    this.subscription = null;
    this.formModel = {
      code: null,
      startDate: null,
      endDate: null,
      totalCredits: null,
      creditsToAdd: null,
      remainingCredits: null,
      articleId: null,
      priceCategoryId: null,
      notifyByEmail: false,
      categoryName: null,
      categoryId: null,
      contactId: null,
    };
  }

  getRecreditStartDate() {
    const category = this.subscription.subscription_category;
    if (category.start_date_type === 'fixed_date') {
      const categoryFixedDAte = moment(category.fixed_start_date);
      const now = moment();
      const finalStartDate = now.isAfter(categoryFixedDAte) ? now : categoryFixedDAte;
      return finalStartDate.format('YYYY-MM-DD');
    }
    return moment().format('YYYY-MM-DD');
  }

  getDefaultEndDate(startDate: string) {
    const category = this.subscription.subscription_category;
    switch (category.end_date_type_value) {
      case 'fixed_date': {
        return category.fixed_end_date;
      }
      case 'fixed_duration': {
        const now = moment();
        const momentStartDate = moment(startDate);
        const finalStartDate = now.isAfter(momentStartDate) ? now : momentStartDate;
        const calculateEndFromDate =
          this.subscription.subscription_category.recredit_date_value === 'subscription_end_date'
            ? this.subscription.end_date
            : finalStartDate;

        return moment(calculateEndFromDate)
          .add(category.fixed_duration_number, category.duration_type_value)
          .format('YYYY-MM-DD');
      }
      case 'without_end_date': {
        return null;
      }
    }
  }
  getDefaultCreditsToAdd() {
    const category = this.subscription.subscription_category;
    return category.number_credits;
  }
  // WATCHERS
  @Watch('strToDetectIfRefreshFormModelIsNeeded')
  onStrToDetectIfRefreshFormModelIsNeededChanged() {
    if (this.showModal) {
      this.getSubscription();
    }
  }
}
